import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Badge } from '@/features/shared/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/features/shared/components/ui/avatar';
import { Users, Network, CalendarOff, Loader2, Building2 } from 'lucide-react';
import {
  getMyOrgSummary, getMySubordinates, getOrgBalances, getTeamOut, getDepartments,
  type Employee, type OrgSummary, type OrgEmployeeBalances,
} from '@/lib/hrApi';
import { levelLabel, levelColor, levelRank } from '../orgLevel';

interface Row {
  emp: Employee;
  status: 'active' | 'on_leave' | 'inactive';
  balances?: OrgEmployeeBalances;
}

const INACTIVE = ['inactive', 'terminated', 'suspended'];

export const OrganizationView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const goToEmployee = (id: string) => {
    const base = location.pathname.replace(/\/employees(\/.*)?$/, '/employees');
    navigate(`${base}/${id}`);
  };

  const [summary, setSummary] = useState<OrgSummary | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [deptMap, setDeptMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [sum, subs, out, deptRes] = await Promise.all([
          getMyOrgSummary().catch(() => null),
          getMySubordinates(true),
          getTeamOut(0).catch(() => [] as any[]),
          getDepartments().then((d) => d.items || []).catch(() => []),
        ]);
        if (!active) return;
        setSummary(sum);
        setDeptMap(new Map<string, string>((deptRes as any[]).map((d) => [d.id, d.name])));

        const outSet = new Set<string>((out as any[]).map((o) => String(o.employee_id)));
        // Resolve balances for everyone who has a linked auth user.
        const userIds = subs.map((e) => e.user_id).filter(Boolean) as string[];
        const balances = userIds.length ? await getOrgBalances(userIds).catch(() => []) : [];
        const balById = new Map(balances.map((b) => [String(b.employee_id), b]));

        const mapped: Row[] = subs.map((emp) => {
          const raw = (emp.status || '').toLowerCase();
          let status: Row['status'] = 'active';
          if (INACTIVE.includes(raw)) status = 'inactive';
          // From the leave dates only — the stored `OnLeave` could outlive the leave.
          else if (emp.user_id && outSet.has(String(emp.user_id))) status = 'on_leave';
          return { emp, status, balances: emp.user_id ? balById.get(String(emp.user_id)) : undefined };
        });
        // Highest org level first, then name.
        mapped.sort((a, b) =>
          levelRank(b.emp.org_level) - levelRank(a.emp.org_level) ||
          `${a.emp.first_name} ${a.emp.last_name}`.localeCompare(`${b.emp.first_name} ${b.emp.last_name}`),
        );
        setRows(mapped);
      } catch {
        if (active) setError('Could not load your organization.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const outNow = useMemo(() => rows.filter((r) => r.status === 'on_leave').length, [rows]);
  const name = (e: Employee) => `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email || 'Unknown';
  const initials = (n: string) => n.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const annual = (b?: OrgEmployeeBalances) => b?.balances.find((x) => x.type_code === 'ANNUAL');

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading your organization…</div>;
  }
  if (error) return <div className="py-12 text-center text-danger">{error}</div>;

  if (rows.length === 0) {
    return (
      <Card className="border-2 border-orange-100 dark:border-orange-500/20">
        <CardContent className="py-12 text-center text-muted-foreground">
          <Network className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          No one reports to you yet. Assign reports (set their manager) to build your organization.
        </CardContent>
      </Card>
    );
  }

  const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardContent className="p-4">
        <p className="text-h2 text-charcoal">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="People in my org" value={summary?.total_reports ?? rows.length} />
        <Stat label="Direct reports" value={summary?.direct_reports ?? '—'} />
        <Stat label="Out today" value={outNow} />
        <Stat label="Managers under me" value={rows.filter((r) => levelRank(r.emp.org_level) >= levelRank('manager')).length} />
      </div>

      {/* By level */}
      {summary?.by_level && Object.keys(summary.by_level).length > 0 && (
        <Card className="border-orange-100 dark:border-orange-500/20">
          <CardContent className="p-4 flex flex-wrap gap-2">
            <span className="text-xs text-muted-foreground self-center mr-1">By level:</span>
            {Object.entries(summary.by_level)
              .sort((a, b) => levelRank(b[0]) - levelRank(a[0]))
              .map(([lvl, n]) => (
                <Badge key={lvl} className={`border ${levelColor(lvl)}`}>{levelLabel(lvl)}: {n}</Badge>
              ))}
          </CardContent>
        </Card>
      )}

      {/* People + leave balances */}
      <Card className="border-2 border-orange-100 dark:border-orange-500/20">
        <CardHeader className="bg-gradient-to-r from-orange-50 dark:from-orange-500/[0.08] to-amber-50 dark:to-amber-500/[0.05]">
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <Users className="w-5 h-5 text-orange-600" /> My organization ({rows.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100">
            {rows.map(({ emp, status, balances }) => {
              const a = annual(balances);
              return (
                <button key={emp.id} type="button" onClick={() => goToEmployee(emp.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50/50 text-left">
                  <Avatar className="w-9 h-9">
                    <AvatarImage src={emp.profile_picture_url || undefined} />
                    <AvatarFallback className="bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 text-xs">{initials(name(emp))}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{name(emp)}</span>
                      <Badge className={`text-[10px] border ${levelColor(emp.org_level)}`}>{levelLabel(emp.org_level)}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {emp.position || '—'}{emp.department_id && deptMap.get(emp.department_id) ? ` · ${deptMap.get(emp.department_id)}` : ''}
                    </p>
                  </div>
                  <div className="hidden sm:flex items-center gap-4 text-right">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{a ? String(Math.round(Number(a.available) * 10) / 10) : '—'}</p>
                      <p className="text-[10px] text-muted-foreground">annual left</p>
                    </div>
                    <div className="w-20">
                      {status === 'on_leave' ? (
                        <Badge className="bg-orange-100 dark:bg-orange-500/[0.12] text-orange-800 border border-orange-200 dark:border-orange-500/25 text-xs flex items-center gap-1 justify-center">
                          <CalendarOff className="w-3 h-3" /> Out
                        </Badge>
                      ) : status === 'inactive' ? (
                        <Badge className="bg-danger-soft text-danger-strong border border-transparent text-xs justify-center">Inactive</Badge>
                      ) : (
                        <Badge className="bg-success-soft text-success-strong border border-transparent text-xs justify-center">Active</Badge>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <Building2 className="w-3 h-3" /> You see everyone in your reporting subtree. Leave balances show for people with a linked account.
      </p>
    </div>
  );
};
